import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, ClockIcon, CheckCircleIcon, EditIcon, TrashIcon, BellIcon, CalendarIcon, PillIcon, ChevronDownIcon, ChevronUpIcon, HistoryIcon, XIcon, ArrowLeftIcon } from 'lucide-react';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { AddReminderModal } from '../components/modals/AddReminderModal';
import { MedicineReminderCard } from '../components/shared/MedicineReminderCard';
import { useStore } from '../lib/store';
import { toast } from 'sonner';
interface Reminder {
  id: string;
  medicineName: string;
  medicineStrength: string;
  medicineForm: string;
  dosage: string;
  frequency: string;
  timesPerDay: number;
  doseTimes: string[];
  daysOfWeek: number[];
  mealTiming: 'before' | 'after' | 'with';
  pillsPerDose: number;
  dosesPerIntake: number;
  currentStock: number;
  autoReorder: boolean;
  nextDose: Date;
  duration: number;
  reminderType: string[];
  notes?: string;
  status: 'active' | 'completed';
  createdAt: Date;
  snoozedUntil?: Date;
}
export function MedicineReminderPage() {
  const navigate = useNavigate();
  const addToCart = useStore(state => state.addToCart);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  // Load reminders from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('medicineReminders');
    if (stored) {
      const parsed = JSON.parse(stored);
      setReminders(parsed.map((r: any) => ({
        ...r,
        nextDose: new Date(r.nextDose),
        createdAt: new Date(r.createdAt)
      })));
    } else {
      // Sample data
      setReminders([{
        id: '1',
        medicineName: 'Panadol Extra',
        medicineStrength: '500mg',
        medicineForm: 'Tablet',
        dosage: '1 tablet',
        frequency: 'Every 8 hours',
        timesPerDay: 3,
        doseTimes: ['08:00', '16:00', '00:00'],
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
        mealTiming: 'after',
        pillsPerDose: 1,
        dosesPerIntake: 1,
        currentStock: 20,
        autoReorder: true,
        nextDose: new Date(Date.now() + 2 * 60 * 60 * 1000),
        duration: 7,
        reminderType: ['push', 'sms'],
        notes: 'Take after meals',
        status: 'active',
        createdAt: new Date()
      }, {
        id: '2',
        medicineName: 'Vitamin D3',
        medicineStrength: '1000 IU',
        medicineForm: 'Capsule',
        dosage: '1 capsule',
        frequency: 'Once daily',
        timesPerDay: 1,
        doseTimes: ['09:00'],
        daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
        mealTiming: 'with',
        pillsPerDose: 1,
        dosesPerIntake: 1,
        currentStock: 30,
        autoReorder: false,
        nextDose: new Date(Date.now() + 5 * 60 * 60 * 1000),
        duration: 30,
        reminderType: ['push'],
        status: 'active',
        createdAt: new Date()
      }]);
    }
  }, []);
  // Save reminders to localStorage
  useEffect(() => {
    if (reminders.length > 0) {
      localStorage.setItem('medicineReminders', JSON.stringify(reminders));
    }
  }, [reminders]);
  const activeReminders = reminders.filter(r => r.status === 'active');
  const completedReminders = reminders.filter(r => r.status === 'completed');
  const handleAddReminder = (reminder: Omit<Reminder, 'id' | 'createdAt'>) => {
    const newReminder: Reminder = {
      ...reminder,
      id: Date.now().toString(),
      createdAt: new Date()
    };
    setReminders([...reminders, newReminder]);
    setShowAddModal(false);
  };
  const handleEditReminder = (id: string, updated: Omit<Reminder, 'id' | 'createdAt'>) => {
    setReminders(reminders.map(r => r.id === id ? {
      ...updated,
      id: r.id,
      createdAt: r.createdAt
    } : r));
    setEditingReminder(null);
    setShowAddModal(false);
  };
  const handleMarkTaken = (id: string) => {
    setReminders(reminders.map(r => r.id === id ? {
      ...r,
      status: 'completed' as const
    } : r));
  };
  const handleDeleteReminder = (id: string) => {
    setReminders(reminders.filter(r => r.id !== id));
  };
  const handleReorder = (reminder: Reminder) => {
    addToCart({
      id: Number(`77${reminder.id}`),
      name: reminder.medicineName,
      price: 24.99,
      image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop',
      requiresPrescription: false,
      nowEligible: true
    });
    toast.success(`${reminder.medicineName} added for availability review`);
    navigate('/cart?mode=now');
  };
  const handleSnooze = (id: string) => {
    const snoozeUntil = new Date(Date.now() + 10 * 60 * 1000); // 10 minutes from now
    setReminders(reminders.map(r => r.id === id ? {
      ...r,
      snoozedUntil: snoozeUntil,
      nextDose: snoozeUntil
    } : r));
  };
  const getNextDoseText = (nextDose: Date) => {
    const now = new Date();
    const diff = nextDose.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff % (1000 * 60 * 60) / (1000 * 60));
    if (diff < 0) return 'Overdue';
    if (hours === 0) return `In ${minutes} min`;
    if (hours < 24) return `In ${hours}h ${minutes}m`;
    return `In ${Math.floor(hours / 24)} days`;
  };
  const sortedActiveReminders = [...activeReminders].sort((a, b) => a.nextDose.getTime() - b.nextDose.getTime());
  const nextReminder = sortedActiveReminders[0];
  return <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-7318" data-mp="1aekbxicq6pmm">
      {/* Desktop Header */}
      <div className="hidden md:block" data-id="element-7319" data-mp="1fcvm5u6xn25d">
        <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-7320" data-mp="1fdx0c7j8w4rb" />
      </div>
      {/* Mobile Internal Header */}
      <div className="fixed inset-x-0 top-0 z-50 md:hidden" data-id="element-7321" data-mp="rocftk7g0zfx">
        <MobileInternalHeader title="Medicine Reminders" data-id="element-7322" data-mp="rlq6bf0zs9v2" />
      </div>
      <main className="flex-grow pt-14 md:pt-[var(--header-height-desktop)]" data-id="element-7323" data-mp="wl2uq47ihozr">
        {/* Page Hero Breadcrumb */}
        <PageHeroBreadcrumb breadcrumbs={[{
        label: 'Home',
        href: '/'
      }, {
        label: 'Medicine Reminders'
      }]} title="Medicine Reminders" description="Never miss a dose. Set up personalized reminders for your medications and track your medication schedule." data-id="element-7324" data-mp="p11yppndz2wk" />
        <div className="container mx-auto px-4 py-8" data-id="element-7325" data-mp="66d7e5sk4qzv">
          {/* Next Dose Alert */}
          {nextReminder && <div className="mb-6 bg-gradient-to-r from-[#1e2b55] to-[#162041] rounded-xl p-4 text-white shadow-lg" data-id="element-7326" data-mp="3sjuy8yjbxr7q">
              <div className="flex items-start justify-between" data-id="element-7327" data-mp="3nla9bv9j2tyl">
                <div className="flex-1" data-id="element-7328" data-mp="3ninst64nt448">
                  <div className="flex items-center gap-2 mb-2" data-id="element-7329" data-mp="3ilxb6ed7i0gp">
                    <BellIcon className="w-5 h-5 text-[#119c4b]" data-id="element-7330" data-mp="3ini8ucuthzis" />
                    <span className="text-sm font-semibold" data-id="element-7331" data-mp="3ekltcv0kqjj2">Next Dose</span>
                  </div>
                  <h3 className="text-lg font-bold mb-1" data-id="element-7332" data-mp="16bx6j6qf3q0m">
                    {nextReminder.medicineName}
                  </h3>
                  <p className="text-sm text-blue-100 mb-3" data-id="element-7333" data-mp="1b84et66addlq">
                    {nextReminder.dosage} •{' '}
                    {getNextDoseText(nextReminder.nextDose)}
                  </p>
                  <div className="flex gap-2" data-id="element-7334" data-mp="11e42okcl783i">
                    <button onClick={() => handleMarkTaken(nextReminder.id)} className="bg-[#119c4b] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0f8a42] transition-colors" data-id="element-7335" data-mp="nkmlv7bac9h6">
                      ✅ Mark as Taken
                    </button>
                    <button onClick={() => handleSnooze(nextReminder.id)} className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-opacity-30 transition-colors" data-id="element-7336" data-mp="imb7qkdyjct5">
                      ⏰ Snooze 10 min
                    </button>
                  </div>
                </div>
                <ClockIcon className="w-12 h-12 text-blue-200 opacity-50" data-id="element-7337" data-mp="3h6o2u6qbaknb" />
              </div>
            </div>}
          {/* Active Reminders */}
          <div className="mb-8" data-id="element-7338" data-mp="3q6asxwdlc412">
            <div className="flex items-center justify-between mb-4" data-id="element-7339" data-mp="3q61bwyaix77i">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-7340" data-mp="8y7cct22w3gh">
                Active Reminders ({activeReminders.length})
              </h2>
              <button onClick={() => setShowAddModal(true)} className="hidden md:flex items-center gap-2 bg-[#1e2b55] text-white px-4 py-2 rounded-lg font-medium hover:bg-[#162041] transition-colors" data-id="element-7341" data-mp="3y1e2u1azipf">
                <PlusIcon className="w-4 h-4" data-id="element-7342" data-mp="dr9zojg9igo8" />
                Add Reminder
              </button>
            </div>
            {activeReminders.length === 0 ? <div className="bg-white rounded-xl p-8 text-center shadow-sm" data-id="element-7343" data-mp="mr5f50guoxu2">
                <PillIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" data-id="element-7344" data-mp="1d95slduigkhd" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-7345" data-mp="18gvwth6psq76">
                  No Active Reminders
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-7346" data-mp="14gls07uns5tw">
                  Start by adding your first medication reminder
                </p>
                <button onClick={() => setShowAddModal(true)} className="bg-[#1e2b55] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#162041] transition-colors" data-id="element-7347" data-mp="zewcog0el600">
                  Add Your First Reminder
                </button>
              </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7348" data-mp="1i7qj2ljynpsm">
                {sortedActiveReminders.map(reminder => <MedicineReminderCard key={reminder.id} reminder={reminder} onMarkTaken={handleMarkTaken} onEdit={r => {
              setEditingReminder(r);
              setShowAddModal(true);
            }} onDelete={handleDeleteReminder} onSnooze={handleSnooze} onReorder={handleReorder} data-id="element-7349" data-mp="bspu4ztcs7vj" />)}
              </div>}
          </div>
          {/* Smart Suggestions */}
          {completedReminders.length > 0 && <div className="mb-8" data-id="element-7350" data-mp="fgz30imse9sj">
              <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-7351" data-mp="kcwn901680al">
                💡 Smart Suggestions
              </h2>
              <div className="bg-white rounded-xl p-4 shadow-sm" data-id="element-7352" data-mp="pe3r4o070fbz">
                <p className="text-sm text-gray-600 mb-3" data-id="element-7353" data-mp="ucf3b5dyn1xd">
                  Based on your history
                </p>
                <div className="space-y-2" data-id="element-7354" data-mp="2r5759n9akc1a">
                  {completedReminders.slice(0, 2).map(reminder => <button key={reminder.id} onClick={() => handleAddReminder({
                ...reminder,
                status: 'active',
                nextDose: new Date(),
                snoozedUntil: undefined
              })} className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-7355" data-mp="2w3s5vnsggxq9">
                      <div className="flex items-center gap-3" data-id="element-7356" data-mp="3703vrdgjsw56">
                        <PillIcon className="w-5 h-5 text-[#1e2b55]" data-id="element-7357" data-mp="322lhr8nobqqb" />
                        <div className="text-left" data-id="element-7358" data-mp="32091r11h7qfw">
                          <p className="text-sm font-medium text-gray-900" data-id="element-7359" data-mp="3gq6l93ognryr">
                            {reminder.medicineName}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-7360" data-mp="3prwca8efkow0">
                            {reminder.dosage} • {reminder.frequency}
                          </p>
                        </div>
                      </div>
                      <PlusIcon className="w-5 h-5 text-[#119c4b]" data-id="element-7361" data-mp="88pgui3d0j57" />
                    </button>)}
                </div>
              </div>
            </div>}
          {/* History Section */}
          <div className="mb-8" data-id="element-7362" data-mp="3vcvjz3aw2143">
            <button onClick={() => setShowHistory(!showHistory)} className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow" data-id="element-7363" data-mp="3vdniomzs8eyl">
              <div className="flex items-center gap-3" data-id="element-7364" data-mp="3qeu22lp787u7">
                <HistoryIcon className="w-5 h-5 text-[#1e2b55]" data-id="element-7365" data-mp="3qgeuo5rydgpf" />
                <h2 className="text-xl font-bold text-gray-900" data-id="element-7366" data-mp="3co8fon0jdfrk">
                  📜 History ({completedReminders.length})
                </h2>
              </div>
              {showHistory ? <ChevronUpIcon className="w-5 h-5 text-gray-400" data-id="element-7367" data-mp="32oq1kn7b4o7f" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-7368" data-mp="37o2vtt0icxk1" />}
            </button>
            {showHistory && <div className="mt-4 space-y-3" data-id="element-7369" data-mp="dv2yybclvk2k">
                {completedReminders.length === 0 ? <div className="bg-white rounded-xl p-6 text-center" data-id="element-7370" data-mp="9w2l778zj7mf">
                    <p className="text-gray-500" data-id="element-7371" data-mp="539e4cq1nn76">No completed reminders yet</p>
                  </div> : completedReminders.map(reminder => <div key={reminder.id} className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-gray-300" data-id="element-7372" data-mp="3rb0z2p3566e2">
                      <div className="flex items-start justify-between" data-id="element-7373" data-mp="3iakvlfmeez97">
                        <div className="flex-1" data-id="element-7374" data-mp="3iaulz4bcv8ff">
                          <div className="flex items-center gap-2 mb-1" data-id="element-7375" data-mp="3dc15r44op3bk">
                            <CheckCircleIcon className="w-5 h-5 text-gray-400" data-id="element-7376" data-mp="1gj7rutw62xeq" />
                            <h3 className="font-semibold text-gray-600" data-id="element-7377" data-mp="1gh53eeanxcc4">
                              {reminder.medicineName}
                            </h3>
                          </div>
                          <p className="text-sm text-gray-500" data-id="element-7378" data-mp="1j3dwl3slurwc">
                            {reminder.dosage} • {reminder.frequency}
                          </p>
                          <p className="text-xs text-gray-400 mt-1" data-id="element-7379" data-mp="1st79c84hhd26">
                            Completed on{' '}
                            {reminder.createdAt.toLocaleDateString()}
                          </p>
                        </div>
                        <button onClick={() => handleDeleteReminder(reminder.id)} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-7380" data-mp="106w94ykshe5w">
                          <TrashIcon className="w-4 h-4" data-id="element-7381" data-mp="1e4kpbt3wvx4m" />
                        </button>
                      </div>
                    </div>)}
              </div>}
          </div>
        </div>
      </main>
      {/* Floating Action Button (Mobile) */}
      <button onClick={() => setShowAddModal(true)} className="md:hidden fixed bottom-20 right-4 w-14 h-14 bg-[#119c4b] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#0f8a42] transition-colors z-40" data-id="element-7382" data-mp="qey72zp9b4yu">
        <PlusIcon className="w-6 h-6" data-id="element-7383" data-mp="zf4i8exhfsuu" />
      </button>
      {/* Add/Edit Reminder Modal */}
      <AddReminderModal isOpen={showAddModal} onClose={() => {
      setShowAddModal(false);
      setEditingReminder(null);
    }} onSave={editingReminder ? reminder => handleEditReminder(editingReminder.id, reminder) : handleAddReminder} initialData={editingReminder} data-id="element-7384" data-mp="uhuvj71h9vap" />
      <Footer data-id="element-7385" data-mp="bpjyvuxf2tl1" />
      <MobileFooterNav data-id="element-7386" data-mp="bp0r371hdlkq" />
    </div>;
}